import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useCtaSheenStyles } from '../hooks/useCtaSheenStyles';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const primaryCtaDef: SlotGroupDefinition = {
  label: 'Primary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const secondaryCtaDef: SlotGroupDefinition = {
  label: 'Secondary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function ThankYouContent() {
  useRevealStyles();
  useCtaSheenStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const primaryCta = useSlotGroup('thankyoucontent.primary_cta', primaryCtaDef);
  const secondaryCta = useSlotGroup('thankyoucontent.secondary_cta', secondaryCtaDef);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up py-16 md:py-24 bg-[var(--vibe-color-background)]"
      style={{
        ['--vantage-cta-sheen-accent' as string]: 'var(--vibe-color-secondary)',
        ['--vantage-cta-sheen-on-accent' as string]: 'var(--vibe-color-text-on-secondary)',
      }}
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path="thankyoucontent.body"
          label="Body Text"
          defaultValue="<p>Thank you for your interest! A team member will reach out to you soon to help you find your dream home.</p>"
          as="div"
          className="font-body text-base md:text-lg leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
        />

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
          <EditableSlotGroup groupPath="thankyoucontent.primary_cta" definition={primaryCtaDef}>
            <a
              href={primaryCta.url || '/floor-plans'}
              target={primaryCta._target || '_self'}
              className="ty-btn vantage-cta-sheen"
            >
              <span className="relative z-10">{primaryCta.label || 'View Floor Plans'}</span>
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="thankyoucontent.secondary_cta" definition={secondaryCtaDef}>
            <a
              href={secondaryCta.url || '/schedule-a-tour'}
              target={secondaryCta._target || '_self'}
              className="ty-btn vantage-cta-sheen"
            >
              <span className="relative z-10">{secondaryCta.label || 'Schedule a Tour'}</span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Body-scale CTA — matches .an-btn / .pet-cta / .po-cta sizing.
           Fill + sheen hover come from .vantage-cta-sheen. */
        .ty-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          padding: 0.875rem 2rem;
          min-width: 200px;
          cursor: pointer;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ThankYouContent',
  displayName: 'Thank You Content',
  tokens: {
    'thankyoucontent.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue: '<p>Thank you for your interest! A team member will reach out to you soon to help you find your dream home.</p>',
    },
  },
  slotGroups: {
    'thankyoucontent.primary_cta': primaryCtaDef,
    'thankyoucontent.secondary_cta': secondaryCtaDef,
  },
};

(ThankYouContent as any).contentSchema = contentSchema;

export default ThankYouContent;
